import { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  Cross,
  X,
} from "lucide-react";
import styles from "./DatePicker.module.css";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Generate time slots in 30-minute intervals
const generateTimeSlots = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute of [0, 30]) {
      const timeString = `${hour % 12 || 12}:${minute
        .toString()
        .padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
      times.push({
        value: `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`,
        display: timeString,
        hour,
        minute,
      });
    }
  }
  return times;
};

const TIME_SLOTS = generateTimeSlots();

const DatePicker = ({
  selectedDate,
  onDateSelect,
  placeholder = "Select date",
  disableFutureDates = true,
  showTime = false,
  onCancelSelect,
  inputStyle,
  autoFormatDate = false,
}) => {
  const today = new Date();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? new Date(selectedDate).getMonth() : today.getMonth()
  );
  const [currentYear, setCurrentYear] = useState(
    selectedDate ? new Date(selectedDate).getFullYear() : today.getFullYear()
  );
  const [selectedTime, setSelectedTime] = useState(
    selectedDate
      ? `${new Date(selectedDate)
          .getHours()
          .toString()
          .padStart(2, "0")}:${new Date(selectedDate)
          .getMinutes()
          .toString()
          .padStart(2, "0")}`
      : "00:00"
  );

  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [monthSearch, setMonthSearch] = useState("");
  const [yearSearch, setYearSearch] = useState("");
  const [dateChanged, setDateChanged] = useState(false);
  const [manualChange, setManualChange] = useState(false);
  const [dateError, setDateError] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 240,
    placement: "bottom",
  });

  const formatDate = (date, showTime) => {
    if (!date) return "";

    const d = new Date(date);

    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();

    if (showTime === false) {
      const result = `${month}-${day}-${year}`;
      return result;
    }

    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = hours.toString().padStart(2, "0");

    return `${month}-${day}-${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const [inputValue, setInputValue] = useState(
    selectedDate ? formatDate(selectedDate, showTime) : ""
  );

  useEffect(() => {
    if (isOpen && !manualChange) return;
    // Don't overwrite the input while the user has a validation error — preserve what they typed
    if (dateError) return;

    if (selectedDate) {
      setInputValue(formatDate(selectedDate, showTime));
    } else {
      setInputValue("");
    }
  }, [selectedDate, isOpen, manualChange, dateError]);

  const containerRef = useRef(null);
  const monthDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const timeListRef = useRef(null);

  const currentYearValue = new Date().getFullYear();
  
  const years = Array.from(
    { length: currentYearValue - 1900 + 1 }, 
    (_, i) => currentYearValue - i // Starting at current year and counting down
  );
  const filteredMonths = MONTHS.filter((month) =>
    month.toLowerCase().includes(monthSearch.toLowerCase())
  );
  const filteredYears = years.filter((year) =>
    year.toString().includes(yearSearch)
  );

  // Convert UTC datetime to local datetime for display
  const UTCToLocal = (utcDate) => {
    if (!utcDate) return null;
    return new Date(utcDate);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        (containerRef.current && containerRef.current.contains(e.target)) ||
        (calendarRef.current && calendarRef.current.contains(e.target))
      ) {
        return;
      }
      setIsOpen(false);
      setShowMonthDropdown(false);
      setShowYearDropdown(false);
      setMonthSearch("");
      setYearSearch("");
    };

    const handleEscapeKey = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setShowMonthDropdown(false);
        setShowYearDropdown(false);
        setMonthSearch("");
        setYearSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setShowMonthDropdown(false);
      setShowYearDropdown(false);
      setMonthSearch("");
      setYearSearch("");
    }
  }, [isOpen]);

  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = Array(firstDay)
      .fill(null)
      .concat([...Array(daysInMonth).keys()].map((d) => d + 1));
    return days;
  };

  const handleDateClick = (day) => {
    let localDate;

    if (showTime) {
      const [hours, minutes] = selectedTime.split(":").map(Number);

      localDate = new Date(currentYear, currentMonth, day, hours, minutes);
    } else {
      localDate = new Date(currentYear, currentMonth, day, 12, 0, 0);
    }

    if (autoFormatDate) {
      const nowPST = dayjs(new Date(new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" })));
      const selected = dayjs(localDate);

      let error = "";
      if (selected.isAfter(nowPST, "day")) {
        error = "Date of birth cannot be a future date.";
      } else if (selected.isAfter(nowPST.subtract(18, "year"), "day")) {
        error = "Patient must be at least 18 years old.";
      } else if (selected.isBefore(nowPST.subtract(120, "year"), "day")) {
        error = "Age cannot be more than 120 years.";
      }

      if (error) {
        setDateError(error);
        setInputValue(formatDate(localDate, showTime));
        setDateChanged(true);
        setIsOpen(false);
        return;
      }

      setDateError("");
    }

    onDateSelect(localDate);
    setDateChanged(true);
    setIsOpen(false);
  };

  const cancelSelect = () => {
    onCancelSelect();
    setDateChanged(false);
    setDateError("");
  };

  const handleTimeSelect = (timeValue) => {
    setSelectedTime(timeValue);

    // If a date is already selected, update the date with new time
    if (selectedDate) {
      const [hours, minutes] = timeValue.split(":").map(Number);
      const currentLocal = UTCToLocal(selectedDate);

      // Create new LOCAL date with updated time
      const localDate = new Date(
        currentLocal.getFullYear(),
        currentLocal.getMonth(),
        currentLocal.getDate(),
        hours,
        minutes
      );

      onDateSelect(localDate);
      setDateChanged(true);
      setIsOpen(false);
    }
  };

  const handleMonthSelect = (index) => {
    setCurrentMonth(index);
    setShowMonthDropdown(false);
    setMonthSearch("");
  };

  const handleYearSelect = (year) => {
    setCurrentYear(year);
    setShowYearDropdown(false);
    setYearSearch("");
  };

  const navigateMonth = (dir) => {
    if (dir === "prev") {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const handleInputChange = (e) => {
    if (autoFormatDate) {
      setDateChanged(true);

      // Strip non-digits, cap at 8, then auto-insert dashes → MM-DD-YYYY
      const digits = e.target.value.replace(/\D/g, "").slice(0, 8);

      let formatted = digits;
      if (digits.length > 4) {
        formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
      } else if (digits.length > 2) {
        formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
      }

      setInputValue(formatted);

      if (digits.length === 8) {
        const parsed = dayjs(formatted, "MM-DD-YYYY", true);
        const nowPST = dayjs(new Date(new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" })));

        if (!parsed.isValid()) {
          setDateError("Invalid date. Please enter a valid MM-DD-YYYY date.");
        } else if (parsed.isAfter(nowPST, "day")) {
          setDateError("Date of birth cannot be a future date.");
        } else if (parsed.isAfter(nowPST.subtract(18, "year"), "day")) {
          setDateError("Patient must be at least 18 years old.");
        } else if (parsed.isBefore(nowPST.subtract(120, "year"), "day")) {
          setDateError("Age cannot be more than 120 years.");
        } else {
          setDateError("");
          const dateObj = parsed.toDate();
          onDateSelect(dateObj);
          if (parsed.month() !== currentMonth) setCurrentMonth(parsed.month());
          if (parsed.year() !== currentYear) setCurrentYear(parsed.year());
        }
      } else {
        setDateError("");
      }
      return;
    }

    // Original behavior for all other DatePicker usages
    console.clear();
    setDateChanged(true);
    let val = e.target.value;

    val = val.toUpperCase().replace(/[^0-9AMP:\- :]/g, "");

    setInputValue(val);

    const formats = showTime ? ["MM-DD-YYYY hh:mm A"] : ["MM-DD-YYYY"];

    const parsed = dayjs(val, formats, true);

    if (parsed.isValid()) {
      setDateChanged(true);
      const dateObj = parsed.toDate();

      onDateSelect(dateObj);
      const month = dateObj.getMonth();
      const year = dateObj.getFullYear();

      if (month !== currentMonth) setCurrentMonth(month);
      if (year !== currentYear) setCurrentYear(year);
    }
  };

  const calendarDays = generateCalendarDays();

  const isTodayDate = (day) => {
    const todayLocal = new Date();
    return (
      day === todayLocal.getDate() &&
      currentMonth === todayLocal.getMonth() &&
      currentYear === todayLocal.getFullYear()
    );
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;

    const selectedLocal = UTCToLocal(selectedDate);
    return (
      day === selectedLocal.getDate() &&
      currentMonth === selectedLocal.getMonth() &&
      currentYear === selectedLocal.getFullYear()
    );
  };

  const isFutureDate = (day) => {
    if (!day) return false;
    const date = new Date(currentYear, currentMonth, day);
    const today = new Date();
    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const openDropdown = () => {
    if (!isOpen) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 280;
      const dropdownWidth = showTime ? 360 : 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let placement = "bottom";
      let top = rect.bottom + window.scrollY + 2;
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        placement = "top";
        top = rect.top + window.scrollY - dropdownHeight - 4;
      }
      setDropdownPosition({
        top,
        left: rect.left + window.scrollX,
        width: dropdownWidth,
        placement,
      });
    }
    setIsOpen((prev) => !prev);
  };

  // const handleDisplayDate = (inputValue) => {
  //   if (!inputValue) return "";


  //   // Parse the input value using dayjs with the current format
  //   const parsed = dayjs(
  //     inputValue,
  //     ["MM-DD-YYYY", "MM-DD-YYYY hh:mm A"],
  //     true
  //   );

  //   if (parsed.isValid()) {
  //     // Format to MM-DD-YYYY or MM-DD-YYYY hh:mm A
  //     if (showTime) {
  //       return parsed.format("MM-DD-YYYY hh:mm A");
  //     } else {
  //       return parsed.format("MM-DD-YYYY");
  //     }
  //   }
  // };

  return (
    <div className={styles.container} ref={containerRef}>
      <div
        className={styles.inputButton}
        style={inputStyle}
        onClick={() => !isOpen && openDropdown()}
      >
        <span
          style={{
            display: "flex",
            gap: "5px",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Calendar size={16} className={styles.dateIcon} />
          <input
            type="text"
            // value={handleDisplayDate(inputValue)}
            value={inputValue}
            onChange={(e) => handleInputChange(e)}
            placeholder={placeholder}
            className={`${styles.datepickerInput} ${
              !isOpen ? styles.datepickerPointer : ""
            }`}
          />
        </span>
        {!isOpen && dateChanged && (
          <X
            size={12}
            className={styles.clearIcon}
            onClick={(e) => {
              e.stopPropagation();
              cancelSelect();
            }}
          />
        )}
      </div>

      {dateError && (
        <span className={styles.dateError}>{dateError}</span>
      )}

      {isOpen &&
        createPortal(
          <div
            className={`${styles.dropdown} ${styles.dropdownOpen} ${
              showTime ? styles.withTime : ""
            }`}
            ref={calendarRef}
            role="dialog"
            aria-label="Date picker"
            style={{
              position: "absolute",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: `${dropdownPosition.width}px`,
              zIndex: 9999,
            }}
          >
            <div className={styles.pickerContainer}>
              {/* Date Picker Section */}
              <div className={styles.dateSection}>
                <div className={styles.header}>
                  <button
                    type="button"
                    onClick={() => navigateMonth("prev")}
                    className={styles.navBtn}
                    aria-label="Previous month"
                  >
                    <ChevronLeft size={12} />
                  </button>

                  <div className={styles.monthYear}>
                    <div className={styles.dropdownWrap}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMonthDropdown(!showMonthDropdown);
                          setShowYearDropdown(false);
                        }}
                        className={`${styles.monthButton} ${
                          showMonthDropdown ? styles.activeDropdown : ""
                        }`}
                        aria-expanded={showMonthDropdown}
                      >
                        {MONTHS[currentMonth].substring(0, 3)}
                      </button>

                      {showMonthDropdown && (
                        <div
                          className={styles.dropdownBox}
                          ref={monthDropdownRef}
                        >
                          <div className={styles.searchWrap}>
                            <input
                              value={monthSearch}
                              onChange={(e) => setMonthSearch(e.target.value)}
                              className={styles.searchInput}
                              placeholder="Search "
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className={styles.scrollList}>
                            {filteredMonths.map((month) => {
                              const index = MONTHS.indexOf(month);
                              return (
                                <button
                                  type="button"
                                  key={month}
                                  data-month={index}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMonthSelect(index);
                                  }}
                                  className={`${styles.dropdownItem} ${
                                    index === currentMonth
                                      ? styles.activeItem
                                      : ""
                                  }`}
                                >
                                  {month}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={styles.dropdownWrap}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowYearDropdown(!showYearDropdown);
                          setShowMonthDropdown(false);
                        }}
                        className={`${styles.yearButton} ${
                          showYearDropdown ? styles.activeDropdown : ""
                        }`}
                        aria-expanded={showYearDropdown}
                      >
                        {currentYear}
                      </button>

                      {showYearDropdown && (
                        <div
                          className={styles.dropdownBox}
                          ref={yearDropdownRef}
                        >
                          <div className={styles.searchWrap}>
                            <input
                              value={yearSearch}
                              onChange={(e) => setYearSearch(e.target.value)}
                              className={styles.searchInput}
                              placeholder="Search "
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className={styles.scrollList}>
                            {filteredYears.map((year) => (
                              <button
                                type="button"
                                key={year}
                                data-year={year}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleYearSelect(year);
                                }}
                                className={`${styles.dropdownItem} ${
                                  year === currentYear ? styles.activeItem : ""
                                }`}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigateMonth("next")}
                    className={styles.navBtn}
                    aria-label="Next month"
                  >
                    <ChevronRight size={12} />
                  </button>
                </div>

                <div className={styles.calendar}>
                  <div className={styles.weekRow}>
                    {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                      <div key={day} className={styles.weekDay}>
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className={styles.daysGrid}>
                    {calendarDays.map((day, i) => (
                      <div key={i} className={styles.dayCell}>
                        {day && (
                          <button
                            type="button"
                            onClick={() =>
                              !disableFutureDates || !isFutureDate(day)
                                ? handleDateClick(day)
                                : undefined
                            }
                            className={`${styles.dayButton} ${
                              isSelected(day)
                                ? styles.selectedDay
                                : isTodayDate(day)
                                ? styles.today
                                : ""
                            } ${
                              disableFutureDates && isFutureDate(day)
                                ? styles.disabledDay
                                : ""
                            }`}
                            aria-label={`Select ${day} ${MONTHS[currentMonth]} ${currentYear}`}
                            disabled={disableFutureDates && isFutureDate(day)}
                          >
                            {day}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {showTime && (
                <div className={styles.timeSection}>
                  <div className={styles.timeHeader}>
                    <Clock size={12} />
                    <span>Time</span>
                  </div>
                  <div className={styles.timeList} ref={timeListRef}>
                    {TIME_SLOTS.map((timeSlot) => (
                      <button
                        key={timeSlot.value}
                        type="button"
                        data-time={timeSlot.value}
                        onClick={() => handleTimeSelect(timeSlot.value)}
                        className={`${styles.timeSlot} ${
                          selectedTime === timeSlot.value
                            ? styles.selectedTime
                            : ""
                        }`}
                      >
                        {timeSlot.display}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DatePicker;
